import type { FormTemplate } from '../template/types'

/**
 * The remembered field values that carry from one signer to the next.
 *
 * localStorage rather than IndexedDB: a handful of short strings, and producer
 * settings rather than anybody's personal data.
 */

const KEY_REMEMBER = 'consent.remember'

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw === null ? fallback : (JSON.parse(raw) as T)
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // A full or blocked storage quota must not stop someone signing.
  }
}

/** Starting values: the template's own defaults, overlaid with what was remembered. */
export function startingValues(template: FormTemplate): Record<string, string> {
  const remembered = read<Record<string, string>>(KEY_REMEMBER, {})
  const values: Record<string, string> = {}
  for (const f of template.fields) {
    const carried = f.remember ? remembered[`${template.id}.${f.key}`] : undefined
    const value = carried ?? f.default
    if (value) values[f.key] = value
  }
  return values
}

export function rememberValues(template: FormTemplate, values: Record<string, string>): void {
  const remembered = read<Record<string, string>>(KEY_REMEMBER, {})
  for (const f of template.fields) {
    if (!f.remember) continue
    remembered[`${template.id}.${f.key}`] = values[f.key] ?? ''
  }
  write(KEY_REMEMBER, remembered)
}
